import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import Cookies from "js-cookie";
import { Link, Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import { appRoutes } from "../routes/appRoutes";
import { useGlobalAuthContext } from "../context/AuthContext";
import { jwtDecode } from "jwt-decode";
import { usePartnersQuery } from "../Slices/roomNateApis";
import { useGlobalContext } from "../context/GlobalContext";
import AdminPartnerSelect from "./AdminPartnerSelect";
import { ReactComponent as LogoIcon } from "../Assets/mf-logo.svg";
import {
  canonicalPathForPartnerChip,
  isAdminEmbedPath,
  resolveAdminNavTo,
} from "../utils/adminEmbedPaths";

const drawerWidth = 240;

const navItemTextColor = "rgba(0,0,0,0.65)";

/**
 * Sidebar active state must not rely on NavLink's `.active` class alone — with MUI ListItemButton,
 * RR7 can leave the wrong row highlighted (e.g. Floor Plans stuck active). Use explicit path matching.
 */
function isSidebarRouteActive(pathname, to) {
  if (!to) return false;
  if (to === "/logs") {
    return (
      pathname === "/logs" ||
      pathname.startsWith("/logs/") ||
      pathname === "/admin/logs" ||
      pathname.startsWith("/admin/logs/")
    );
  }
  return (
    matchPath({ path: to, end: true }, pathname) != null ||
    matchPath({ path: `/admin${to}`, end: true }, pathname) != null
  );
}

// Shared style for the fixed floating action buttons (3-dot, support)
const floatingBtnSx = (brandColor) => ({
  bgcolor: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  borderRadius: "50%",
  width: 40,
  height: 40,
  color: brandColor,
  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover": {
    bgcolor: "grey.50",
    boxShadow: "0 4px 12px rgba(0,0,0,0.16)",
  },
});

function ResponsiveDrawer(props) {
  const { currentUserSessionStorage, Logout, brandingColor } =
    useGlobalAuthContext();
  const { activePartner, setActivePartner } = useGlobalContext();
  const navigate = useNavigate();

  const Login = () => navigate("/login");

  const brandColor = brandingColor || "#006766";

  const location = useLocation();
  // Routes where the child fills the full viewport (no padding, no scroll)
  const isAdminViewerRoute = [
    "/admin/floor-plan/:layoutId/version/:version",
    "/admin/floor-gallery/:galleryId",
    "/admin/gallery/:id",
    "/admin/layout/:id",
  ].some((path) => matchPath({ path, end: true }, location.pathname) != null);
  const isFullBleedRoute =
    location.pathname === "/external-ai-upload" ||
    location.pathname === "/admin/external-ai-upload" ||
    isAdminViewerRoute;
  // Routes where activePartner actually filters data — show the chip only here
  const PARTNER_FILTERED_ROUTES = [
    "/",
    "/home",
    "/layouts",
    "/floorPlans",
    "/users",
    "/partners",
    "/affiliate-products",
    "/products",
  ];
  const pathForPartnerChip = canonicalPathForPartnerChip(location.pathname);
  const showPartnerChip = PARTNER_FILTERED_ROUTES.some(
    (r) =>
      pathForPartnerChip === r ||
      (r !== "/" && pathForPartnerChip.startsWith(r + "/"))
  );

  React.useEffect(() => {
    if (isAdminEmbedPath(location.pathname)) {
      document.body.classList.add("embed-mode");
      return () => document.body.classList.remove("embed-mode");
    }
    return undefined;
  }, [location.pathname]);

  const { window: localWindow } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // Desktop sidebar open/closed — persists across navigations
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const userRole = React.useMemo(
    () =>
      currentUserSessionStorage
        ? jwtDecode(currentUserSessionStorage)?.user?.role
        : null,
    [currentUserSessionStorage]
  );

  const isAdmin = userRole === "admin";

  // Non-admins must never retain a client-controlled partner scope (IDOR risk).
  React.useEffect(() => {
    if (!currentUserSessionStorage) return;
    if (!isAdmin && activePartner) {
      setActivePartner("");
      Cookies.remove("activePartner");
    }
  }, [currentUserSessionStorage, isAdmin, activePartner, setActivePartner]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  // Partner selector
  const { data: partnersData } = usePartnersQuery("", {
    skip: !currentUserSessionStorage || !isAdmin,
  });

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* ── Header: logo + desktop collapse button ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <LogoIcon width={36} height={36} style={{ flexShrink: 0 }} />

        {/* Collapse sidebar — desktop only */}
        <Tooltip title="Hide sidebar" placement="right">
          <IconButton
            size="small"
            onClick={() => setSidebarOpen(false)}
            aria-label="Hide sidebar"
            sx={{
              display: { xs: "none", sm: "flex" },
              color: "text.disabled",
              borderRadius: "50%",
              "&:hover": { bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary" },
            }}
          >
            <ViewSidebarOutlinedIcon
              sx={{ fontSize: 22, transform: "scaleX(-1)" }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Partner selector — at top, below logo ── */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <AdminPartnerSelect />
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      {/* ── Navigation links — iOS-style flat list ── */}
      <Box sx={{ px: 0.75, flexGrow: 0 }}>
        {appRoutes.map((item, index) => {
          if (item.type === "divider") {
            return <Divider key={`d-${index}`} sx={{ my: 0.75, mx: 1 }} />;
          }
          if (!currentUserSessionStorage || item.role !== userRole) return null;

          const navActive = isSidebarRouteActive(location.pathname, item.to);
          const navTo = resolveAdminNavTo(location.pathname, item.to);

          return (
            <Link
              key={item.to}
              to={navTo}
              onClick={() => {
                if (mobileOpen) setMobileOpen(false);
              }}
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "block",
                width: "100%",
              }}
            >
              <ListItemButton
                component="span"
                selected={navActive}
                sx={{
                  borderRadius: "10px",
                  mb: "2px",
                  py: 0.875,
                  px: 1.25,
                  color: navItemTextColor,
                  textDecoration: "none",
                  minHeight: 0,
                  width: "100%",
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.04)",
                    color: "rgba(0,0,0,0.85)",
                  },
                  "&.Mui-selected": {
                    bgcolor: alpha(brandColor, 0.1),
                    color: navItemTextColor,
                    "& .MuiListItemText-primary": {
                      fontWeight: 600,
                      color: navItemTextColor,
                    },
                    "& .MuiListItemIcon-root": { color: navItemTextColor },
                  },
                  "& .MuiListItemIcon-root": {
                    color: navItemTextColor,
                    minWidth: 34,
                  },
                }}
              >
                {item.icon ? (
                  <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
                ) : null}
                <ListItemText
                  primary={item.name}
                  inset={false}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    letterSpacing: "-0.01em",
                    lineHeight: 1.4,
                  }}
                  sx={item.icon ? undefined : { pl: 0 }}
                />
              </ListItemButton>
            </Link>
          );
        })}
      </Box>

      {/* ── Spacer pushes auth row to bottom ── */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }} />

      <Divider />

      {/* ── Logout / Login — same row height as nav items, pinned to drawer bottom ── */}
      <Box sx={{ px: 0.75, pt: 0.5, pb: 1.5, flexShrink: 0 }}>
        <ListItemButton
          onClick={() => (currentUserSessionStorage ? Logout(true) : Login())}
          sx={{
            borderRadius: "10px",
            mb: "2px",
            py: 0.875,
            px: 1.25,
            minHeight: 0,
            color: navItemTextColor,
            textDecoration: "none",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.04)",
              color: "rgba(0,0,0,0.85)",
            },
          }}
        >
          <ListItemText
            primary={currentUserSessionStorage ? "Logout" : "Login"}
            inset={false}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.4,
            }}
            sx={{ pl: 0 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  const container =
    localWindow !== undefined ? () => window().document.body : undefined;

  // /start opens this route in a lightbox iframe. Skip the app shell so the
  // frame is only the upload card.
  const isUploadLightbox =
    location.pathname === "/external-ai-upload" &&
    new URLSearchParams(location.search).get("lightbox") === "1";
  if (isUploadLightbox) {
    return <Outlet />;
  }

  // ─── Admin layout ────────────────────────────────────────────────────────────
  if (isAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          overflow: "hidden",
        }}
      >
        {/* ── Mobile 3-dot / X — top-right, xs only ── */}
        <IconButton
          aria-label={mobileOpen ? "close navigation" : "open navigation"}
          onClick={handleDrawerToggle}
          sx={{
            display: { xs: "flex", sm: "none" },
            position: "fixed",
            top: 12,
            right: 12,
            zIndex: 1400,
            ...floatingBtnSx(brandColor),
          }}
        >
          {mobileOpen ? <CloseIcon fontSize="small" /> : <MoreVertIcon fontSize="small" />}
        </IconButton>

        {/* ── Desktop sidebar re-expand — shown only when sidebar is closed ── */}
        {!sidebarOpen && (
          <Tooltip title="Show sidebar" placement="right">
            <IconButton
              onClick={() => setSidebarOpen(true)}
              aria-label="Show sidebar"
              sx={{
                display: { xs: "none", sm: "flex" },
                position: "fixed",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1300,
                ...floatingBtnSx(brandColor),
              }}
            >
              <ViewSidebarOutlinedIcon
                sx={{ fontSize: 22, transform: "scaleX(-1)" }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* ── Support — sticky bottom-right, all admin screens ── */}
        <Tooltip title="Support" placement="left">
          <IconButton
            onClick={() =>
              window.open("https://www.magicfurnish.com/support", "_blank")
            }
            sx={{
              position: "fixed",
              bottom: 12,
              right: 12,
              zIndex: 1300,
              ...floatingBtnSx(brandColor),
            }}
          >
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* ── Partner chip — only on routes where it filters data ── */}
        {showPartnerChip && (() => {
          const selectedTitle =
            activePartner
              ? partnersData?.find((p) => p._id === activePartner)?.title ?? "…"
              : "All Partners";
          const hasPartner = Boolean(activePartner);
          return (
            <Box
              sx={{
                position: "fixed",
                // Sit above the support button cluster at bottom-right,
                // clear of any top-of-page toggles
                bottom: 64,
                right: 12,
                zIndex: 1200,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.625,
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: "10px",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: hasPartner ? brandColor : "rgba(0,0,0,0.18)",
                  transition: "background-color 0.2s ease",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: hasPartner ? 600 : 400,
                  color: hasPartner ? brandColor : "text.disabled",
                  letterSpacing: "-0.01em",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  transition: "color 0.2s ease",
                }}
              >
                {selectedTitle}
              </Typography>
            </Box>
          );
        })()}

        {/* ── Sidebar + content row ── */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Sidebar nav — reserves horizontal flex space */}
          <Box
            component="nav"
            sx={{
              width: { sm: sidebarOpen ? drawerWidth : 0 },
              flexShrink: 0,
              position: "relative",
              zIndex: (theme) => theme.zIndex.drawer,
              transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            aria-label="admin navigation"
          >
            {/* Mobile: temporary overlay drawer */}
            <Drawer
              container={container}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: "block", sm: "none" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                },
              }}
            >
              {drawer}
            </Drawer>

            {/* Desktop: permanent sidebar — slides in/out */}
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: "none", sm: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                  transform: sidebarOpen
                    ? "none"
                    : `translateX(-${drawerWidth}px)`,
                  visibility: sidebarOpen ? "visible" : "hidden",
                  transition:
                    "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.25s !important",
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>

          {/* Right column: scrollable content (stay under nav in z-order so overlapping hit targets don’t steal sidebar clicks) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              position: "relative",
              zIndex: 0,
            }}
          >

            {/* Scrollable page content */}
            <Box
              component="main"
              sx={{
                flex: 1,
                minHeight: 0,
                display: isAdminViewerRoute ? "flex" : "block",
                flexDirection: isAdminViewerRoute ? "column" : undefined,
                overflowY: isFullBleedRoute ? "hidden" : "auto",
                p: isFullBleedRoute ? 0 : 3,
              }}
            >
              {/* Remount route content when pathname changes so client-side nav always shows the correct page */}
              <Outlet key={location.pathname} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  /** `/main` bento fills the viewport; scroll only inside the library panel (not the page). */
  const mainDashboardFill = location.pathname === "/main";

  // ─── Non-admin layout (user / guest) — scrollable main for long home + mobile ─
  return (
    <Box
      sx={{
        height: "100dvh",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: mainDashboardFill
            ? { xs: "auto", md: "hidden" }
            : "auto",
          overflowX: "hidden",
          display: mainDashboardFill ? "flex" : "block",
          flexDirection: mainDashboardFill ? "column" : undefined,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};

export default ResponsiveDrawer;
